import {
    cloneElement,
    isValidElement,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { OverlayOwnerContext, focusableElements } from "@/components/overlay";
import type { TableAction } from "@/components/Table";

interface RowActionsProps<T> {
    row: T;
    actions: TableAction<T>[];
    position: "left" | "right";
    menu: boolean;
}

export const RowActions = <T,>({
    row,
    actions,
    position,
    menu,
}: RowActionsProps<T>) => {
    const { t } = useTranslation("global");
    const owner = useContext(OverlayOwnerContext);
    const [open, setOpen] = useState(false);
    const [coordinates, setCoordinates] = useState({ top: 0, left: 0 });
    const trigger = useRef<HTMLButtonElement>(null);
    const popup = useRef<HTMLDivElement>(null);
    const visible = actions.filter(
        (action) => !action.show || action.show(row)
    );
    const close = (restore = true): void => {
        setOpen(false);
        if (restore) {
            trigger.current?.focus();
        }
    };
    useLayoutEffect(() => {
        if (!open || !popup.current || !trigger.current) {
            return;
        }
        const anchor = trigger.current.getBoundingClientRect();
        const box = popup.current.getBoundingClientRect();
        const left =
            position === "right" ? anchor.right - box.width : anchor.left;
        setCoordinates({
            left: Math.max(
                8,
                Math.min(left, window.innerWidth - box.width - 8)
            ),
            top: Math.max(
                8,
                anchor.bottom + box.height + 6 <= window.innerHeight
                    ? anchor.bottom + 6
                    : anchor.top - box.height - 6
            ),
        });
        focusableElements(popup.current)[0]?.focus();
    }, [open, position]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const outside = (event: MouseEvent): void => {
            if (
                !popup.current?.contains(event.target as Node) &&
                !trigger.current?.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };
        const move = (event: Event): void => {
            if (!popup.current?.contains(event.target as Node)) {
                close();
            }
        };
        document.addEventListener("mousedown", outside);
        window.addEventListener("resize", move);
        window.addEventListener("scroll", move, true);
        return () => {
            document.removeEventListener("mousedown", outside);
            window.removeEventListener("resize", move);
            window.removeEventListener("scroll", move, true);
        };
    }, [open]);
    const keyboard = (event: React.KeyboardEvent): void => {
        const items = popup.current ? focusableElements(popup.current) : [];
        const current = items.indexOf(document.activeElement as HTMLElement);
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            close();
        }
        if (event.key === "Tab") {
            event.preventDefault();
            const root =
                trigger.current?.closest<HTMLElement>("[role=dialog]") ||
                document.body;
            const controls = focusableElements(root).filter(
                (element) => !popup.current?.contains(element)
            );
            const index = controls.indexOf(trigger.current!);
            close(false);
            controls[
                (index + (event.shiftKey ? -1 : 1) + controls.length) %
                    controls.length
            ]?.focus();
        }
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            const index =
                event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? items.length - 1
                      : (current +
                            (event.key === "ArrowDown" ? 1 : -1) +
                            items.length) %
                        items.length;
            items[index]?.focus();
        }
    };
    const icon = (action: TableAction<T>): React.ReactNode => {
        if (typeof action.icon === "string") {
            return <Icon value={action.icon} size={16} />;
        }
        if (isValidElement(action.icon)) {
            return cloneElement(
                action.icon as ReactElement<{
                    size?: number;
                    className?: string;
                }>,
                { size: 16, className: "shrink-0" }
            );
        }
        return <span className="w-4" aria-hidden="true" />;
    };
    if (!visible.length) {
        return null;
    }
    if (!menu) {
        return (
            <div className="flex items-center gap-1">
                {visible.map((action, index) => (
                    <button
                        key={index}
                        type="button"
                        title={action.label}
                        aria-label={action.label}
                        className={`rounded-md p-2 ${action.variant === "delete" ? "text-rose-600" : "text-slate-600 dark:text-zinc-200"}`}
                        onClick={() => action.onClick?.(row)}
                    >
                        {icon(action)}
                    </button>
                ))}
            </div>
        );
    }
    return (
        <>
            <button
                ref={trigger}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t("table.actions")}
                title={t("table.actions")}
                onClick={() => setOpen((previous) => !previous)}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${open ? "bg-slate-200 text-slate-900 dark:bg-white/15 dark:text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"}`}
            >
                <Icon value="moreVertical" size={16} />
            </button>
            {open &&
                createPortal(
                    <div
                        ref={popup}
                        role="menu"
                        aria-label={t("table.actions")}
                        data-overlay-owner={owner || undefined}
                        onKeyDown={keyboard}
                        className="fixed z-[130000] max-h-[calc(100dvh-1rem)] w-max min-w-44 max-w-[min(18rem,calc(100vw-1rem))] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl dark:border-white/15 dark:bg-dark-box"
                        style={coordinates}
                    >
                        {visible.map((action, index) => (
                            <button
                                key={index}
                                type="button"
                                role="menuitem"
                                className={`flex min-h-9 w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium ${action.variant === "delete" ? "text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40" : "text-slate-700 hover:bg-slate-100 dark:text-zinc-200 dark:hover:bg-white/10"}`}
                                onClick={() => {
                                    close();
                                    action.onClick?.(row);
                                }}
                            >
                                {icon(action)}
                                <span>{action.label}</span>
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
};
